import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Ticket } from 'lucide-react';
import { AdvanceBatch } from '@/types';
import { formatDateByDayjs, formatWithThousandSeparator } from '@/utils';
import { DESIGN_PILL_STYLE, isMoneyDashStatus, toDesignStatus } from './designStatus';
import { rejectionReasonLabel } from './format';

const GRID = 'grid-cols-[2.1fr_1.1fr_1.2fr_1.2fr_1.2fr_1fr_0.9fr]';
const DASH = '—';

interface AdvanceBatchesTableProps {
  batches: AdvanceBatch[];
  total: number;
  page: number;
  perPage: number;
  loading?: boolean;
  canTicket: boolean;
  onPageChange: (page: number) => void;
  onTicket: (batch: AdvanceBatch) => void;
  onRowClick: (batch: AdvanceBatch) => void;
}

function MoneyCell({
  value,
  dash,
  color,
  weight,
}: {
  value: string | null | undefined;
  dash: boolean;
  color: string;
  weight: number;
}) {
  const { t } = useTranslation();
  const content = dash ? (
    DASH
  ) : (
    <bdi dir='ltr' style={{ fontVariantNumeric: 'tabular-nums' }}>
      {formatWithThousandSeparator(Number(value ?? 0))} {String(t('iqd'))}
    </bdi>
  );
  return (
    <div className='text-end text-[14px]' style={{ color, fontWeight: weight }}>
      {content}
    </div>
  );
}

/**
 * "Advance batches" table (DESIGN State A §5) — 7-column grid, 5 status pills
 * (exact design colors), em-dash money cells for rejected/queued rows,
 * rejection reason surfaced as pill subtext (design gap fix), a yellow Ticket
 * action per row, and the house-style paginated footer ("Show N/N records").
 */
function AdvanceBatchesTable({
  batches,
  total,
  page,
  perPage,
  loading,
  canTicket,
  onPageChange,
  onTicket,
  onRowClick,
}: AdvanceBatchesTableProps) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const shownTo = Math.min(page * perPage, total);

  return (
    <div className='mb-4 overflow-hidden rounded-2xl border border-solid border-[#EBEBE6] bg-white'>
      <div className='px-[22px] pb-3.5 pt-[18px]'>
        <div className='text-[16px] font-extrabold tracking-[-0.3px] text-[#1B1B18]'>
          {String(t('advance_cod_batches_title'))}
        </div>
        <div className='mt-1 text-[12.5px] text-[#9A9A92]'>
          {String(t('advance_cod_batches_subtitle'))}
        </div>
      </div>

      {/* header row */}
      <div
        className={`grid ${GRID} border-y border-solid border-[#EFEFEA] bg-[#FAFAF7] px-[22px] py-[11px] text-[11.5px] font-bold uppercase tracking-[0.4px] text-[#9A9A92]`}
      >
        <div>{String(t('advance_cod_batch'))}</div>
        <div>{String(t('status'))}</div>
        <div className='text-end'>{String(t('advance_cod_received'))}</div>
        <div className='text-end'>{String(t('advance_cod_repaid_to_ffc'))}</div>
        <div className='text-end'>{String(t('advance_cod_outstanding'))}</div>
        <div className='text-end'>{String(t('advance_cod_loan_fee'))}</div>
        <div className='text-end'>{String(t('actions'))}</div>
      </div>

      {loading && batches.length === 0 ? (
        <div className='flex justify-center py-12'>
          <div className='h-7 w-7 animate-spin rounded-full border-2 border-[rgb(237,224,74)] border-t-transparent' />
        </div>
      ) : batches.length === 0 ? (
        <div className='px-[22px] py-12 text-center'>
          <p className='m-0 text-[13px] font-bold text-[#1B1B18]'>
            {String(t('advance_cod_no_batches_title'))}
          </p>
          <p className='m-0 mt-1 text-[12px] text-[#9A9A92]'>
            {String(t('advance_cod_no_batches_body'))}
          </p>
        </div>
      ) : (
        batches.map((batch) => {
          const design = toDesignStatus(batch.status);
          const dash = isMoneyDashStatus(design);
          const pill = DESIGN_PILL_STYLE[design];
          const received = batch.received_amount ?? batch.disbursed_amount;
          return (
            <div
              key={batch.uid}
              className={`grid ${GRID} cursor-pointer items-center border-b border-solid border-[#F2F2ED] px-[22px] py-4 transition-colors hover:bg-[#FAFAF7]`}
              onClick={() => onRowClick(batch)}
              data-testid='advance-cod-batch-row'
            >
              {/* Batch id + date */}
              <div>
                <div className='text-[14.5px] font-bold tracking-[-0.2px] text-[#1B1B18]'>
                  <bdi>{batch.platform_code}</bdi>
                </div>
                <div className='mt-0.5 text-[12.5px] text-[#9A9A92]'>
                  <bdi>{formatDateByDayjs(batch.created_at, 'D MMMM YYYY')}</bdi>
                </div>
              </div>
              {/* Status pill (+ rejection reason subtext) */}
              <div className='flex flex-col items-start gap-1'>
                <span
                  className='inline-flex items-center rounded-[20px] px-[11px] py-[5px] text-[12px] font-bold'
                  style={{ background: pill.background, color: pill.color }}
                >
                  {String(t(`advance_cod_design_status.${design}`))}
                </span>
                {/* Rejected/failed rows surface a localized reason: known
                    rejection_reason_code slugs map to advance_cod_rejection.*,
                    `other`/unknown codes fall back to the raw friendly reason,
                    and legacy code-less payloads fall back to the mapped
                    failure_reason — so a failed batch never shows a raw code. */}
                {design === 'rejected' ? (
                  <span className='text-[11px] leading-tight text-[#C0392B]'>
                    {rejectionReasonLabel(
                      t as (key: string) => string,
                      batch.rejection_reason_code,
                      batch.rejection_reason,
                      batch.failure_reason,
                    )}
                  </span>
                ) : null}
              </div>
              {/* money cells */}
              <MoneyCell value={received} dash={dash} color='#1B1B18' weight={700} />
              <MoneyCell
                value={batch.repaid_to_ffc_amount}
                dash={dash}
                color='#15794B'
                weight={600}
              />
              <MoneyCell
                value={batch.outstanding_amount}
                dash={dash}
                color='#4A4A44'
                weight={600}
              />
              <MoneyCell value={batch.loan_fee_amount} dash={dash} color='#7A7A72' weight={600} />
              {/* Ticket action */}
              <div className='text-end'>
                <button
                  type='button'
                  disabled={!canTicket}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTicket(batch);
                  }}
                  className='inline-flex items-center gap-1.5 rounded-[9px] border-0 bg-[rgb(237,224,74)] px-[13px] py-2 text-[12.5px] font-bold text-[#1B1B18] transition-colors hover:bg-[rgb(230,217,60)] disabled:cursor-not-allowed disabled:opacity-50'
                >
                  <Ticket className='h-3.5 w-3.5' strokeWidth={1.9} />
                  {String(t('ticket'))}
                </button>
              </div>
            </div>
          );
        })
      )}

      {/* footer / pagination */}
      {batches.length > 0 ? (
        <div className='flex items-center justify-end gap-3.5 px-[22px] py-4'>
          <span className='text-[13px] font-semibold text-[#9A9A92]'>
            {String(t('advance_cod_show_records', { shown: shownTo, total }))}
          </span>
          <div className='flex gap-1.5'>
            <button
              type='button'
              aria-label={String(t('previous'))}
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              className='inline-flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-solid border-[#EBEBE6] bg-white disabled:cursor-not-allowed disabled:opacity-40'
            >
              <ChevronLeft
                className='h-[15px] w-[15px] text-[#B4B4AC] rtl:rotate-180'
                strokeWidth={2.2}
              />
            </button>
            <span className='inline-flex h-[34px] min-w-[34px] items-center justify-center rounded-[9px] bg-[rgb(237,224,74)] px-2 text-[13.5px] font-bold text-[#1B1B18]'>
              {page}
            </span>
            <button
              type='button'
              aria-label={String(t('next'))}
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              className='inline-flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-solid border-[#EBEBE6] bg-white disabled:cursor-not-allowed disabled:opacity-40'
            >
              <ChevronRight
                className='h-[15px] w-[15px] text-[#B4B4AC] rtl:rotate-180'
                strokeWidth={2.2}
              />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdvanceBatchesTable;
